/**
 * Keeps a `PUT user/preferences` body small enough for the column that stores it.
 *
 * `user.preferences` is a MySQL TEXT column (65,535 bytes). A longer value is cut short into
 * invalid JSON with no error, and with it goes everything in the blob — the onboarding resume
 * point included. The wizard's Students screen is what grows it: every student row (with its
 * subjects) is mirrored into `OnboardingWizard.setup.students`, so a school with a few hundred
 * students passes the limit. The cap sits well under the column because MySQL re-serialises
 * the JSON with spaces when `onboarding/save-progress` merges into it.
 */
export const PREFERENCES_MAX_BYTES = 50000

/**
 * Wizard buckets that can be left out of a save, in the order they go: the student list first
 * (the rest of the onboarding still resumes; the students saved with Next are in the school),
 * then the classes draft (only a fallback for classes not yet sent).
 */
const DROPPABLE_WIZARD_PATHS: ReadonlyArray<readonly string[]> = [
  ['setup', 'students'],
  ['setup', 'deletedStudents'],
  ['classesDraft'],
]

const isPlainObject = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

export const preferencesByteLength = (preferences: unknown): number =>
  new TextEncoder().encode(JSON.stringify(preferences ?? {})).length

/** A copy of `preferences` without `OnboardingWizard.<path>`; the same object when absent. */
const withoutWizardPath = (
  preferences: Record<string, any>,
  path: readonly string[]
): Record<string, any> => {
  const wizard = preferences.OnboardingWizard
  if (!isPlainObject(wizard)) return preferences
  const [first, second] = path
  if (second === undefined) {
    if (!(first in wizard)) return preferences
    const {[first]: _dropped, ...rest} = wizard
    return {...preferences, OnboardingWizard: rest}
  }
  const bucket = wizard[first]
  if (!isPlainObject(bucket) || !(second in bucket)) return preferences
  const {[second]: _dropped, ...rest} = bucket
  return {...preferences, OnboardingWizard: {...wizard, [first]: rest}}
}

/**
 * `preferences` as it can be stored: unchanged when it fits, otherwise without the droppable
 * wizard buckets (one at a time, until it fits). Null when it still does not fit — the save
 * must then be refused, since sending it would corrupt the whole column.
 */
export const fitPreferencesForStorage = <T>(preferences: T): T | null => {
  if (!isPlainObject(preferences)) return preferences
  if (preferencesByteLength(preferences) <= PREFERENCES_MAX_BYTES) return preferences
  let fitted: Record<string, any> = preferences
  for (const path of DROPPABLE_WIZARD_PATHS) {
    fitted = withoutWizardPath(fitted, path)
    if (preferencesByteLength(fitted) <= PREFERENCES_MAX_BYTES) return fitted as T
  }
  return null
}
